import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Checkbox,
  FormControlLabel,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface Pontaj {
  id: string;
  angajat: string;
  functie: string;
  parohieId?: string;
  data: string;
  oraIntrare: string;
  oraIesire: string;
  oreLucrate: number;
  oreSuplimentare: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  observatii?: string;
  aprobatDe?: string;
  dataAprobare?: string;
}

const Pontaje: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAngajat, setFilterAngajat] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPontaj, setSelectedPontaj] = useState<Pontaj | null>(null);
  const [showOnlyMyPontaje, setShowOnlyMyPontaje] = useState(false);

  // Mock data pentru pontaje
  const [pontaje] = useState<Pontaj[]>([
    {
      id: '1',
      angajat: 'Părintele Gheorghe',
      functie: 'Paroh',
      parohieId: 'parohia1',
      data: '2024-02-01',
      oraIntrare: '08:00',
      oraIesire: '16:00',
      oreLucrate: 8,
      oreSuplimentare: 0,
      status: 'approved',
      observatii: 'Program normal',
      aprobatDe: 'Episcopul Ioan',
      dataAprobare: '2024-02-02'
    },
    {
      id: '2',
      angajat: 'Secretarul Ana',
      functie: 'Secretar',
      parohieId: 'parohia1',
      data: '2024-02-01',
      oraIntrare: '09:00',
      oraIesire: '17:30',
      oreLucrate: 8,
      oreSuplimentare: 0.5,
      status: 'submitted',
      observatii: 'Ore suplimentare pentru pregătirea documentelor'
    },
    {
      id: '3',
      angajat: 'Contabilul Maria',
      functie: 'Contabil',
      parohieId: 'parohia2',
      data: '2024-02-01',
      oraIntrare: '08:30',
      oraIesire: '15:30',
      oreLucrate: 7,
      oreSuplimentare: 0,
      status: 'draft',
      observatii: 'Plecat mai devreme pentru consultație medicală'
    },
    {
      id: '4',
      angajat: 'Părintele Vasile',
      functie: 'Paroh',
      parohieId: 'parohia3',
      data: '2024-02-01',
      oraIntrare: '07:30',
      oraIesire: '18:00',
      oreLucrate: 8,
      oreSuplimentare: 2,
      status: 'rejected',
      observatii: 'Ore suplimentare nejustificate',
      aprobatDe: 'Vicarul Mihai',
      dataAprobare: '2024-02-02'
    }
  ]);

  const statusOptions = ['draft', 'submitted', 'approved', 'rejected'];
  const angajatiOptions = Array.from(new Set(pontaje.map(p => p.angajat)));

  const filteredPontaje = pontaje.filter(pontaj => {
    const matchesSearch = pontaj.angajat.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pontaj.functie.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || pontaj.status === filterStatus;
    const matchesAngajat = !filterAngajat || pontaj.angajat === filterAngajat;
    const matchesMyPontaje = !showOnlyMyPontaje || 
      (user?.role === 'paroh' && pontaj.parohieId === user.parohieId) ||
      (user?.role === 'episcop' || user?.role === 'vicar');
    
    return matchesSearch && matchesStatus && matchesAngajat && matchesMyPontaje;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'submitted': return 'warning';
      case 'draft': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Ciornă';
      case 'submitted': return 'Trimis';
      case 'approved': return 'Aprobat';
      case 'rejected': return 'Respins';
      default: return status;
    }
  };

  const handleViewPontaj = (pontaj: Pontaj) => {
    setSelectedPontaj(pontaj);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPontaj(null);
  };

  const handleApprove = (pontajId: string) => {
    // Implementare aprobare pontaj
    console.log('Aprobare pontaj:', pontajId);
  };

  const handleReject = (pontajId: string) => {
    // Implementare respingere pontaj
    console.log('Respingere pontaj:', pontajId);
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  const totalPontaje = pontaje.length;
  const pontajeInAsteptare = pontaje.filter(p => p.status === 'submitted').length;
  const totalOreLucrate = pontaje.reduce((sum, p) => sum + p.oreLucrate, 0);
  const totalOreSuplimentare = pontaje.reduce((sum, p) => sum + p.oreSuplimentare, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Pontaje
        </Typography>
        {hasPermission('pontaje.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare pontaj nou */}}
          >
            Pontaj nou
          </Button>
        )}
      </Box>

      {/* Statistici */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Total pontaje
                </Typography>
              </Box>
              <Typography variant="h5">
                {totalPontaje}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TimeIcon color="warning" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  În așteptare
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {pontajeInAsteptare}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Ore lucrate
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {totalOreLucrate}h
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WorkIcon color="info" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Ore suplimentare
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                {totalOreSuplimentare}h
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Toate pontajele" />
          <Tab label="În așteptare" />
          <Tab label="Aprobate" />
          <Tab label="Respinse" />
        </Tabs>

        {/* Tab Toate pontajele */}
        <TabPanel value={activeTab} index={0}>
          {/* Filtre */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Caută pontaje..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {statusOptions.map(status => (
                      <MenuItem key={status} value={status}>
                        {getStatusLabel(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Angajat</InputLabel>
                  <Select
                    value={filterAngajat}
                    onChange={(e) => setFilterAngajat(e.target.value)}
                    label="Angajat"
                  >
                    <MenuItem value="">Toți</MenuItem>
                    {angajatiOptions.map(angajat => (
                      <MenuItem key={angajat} value={angajat}>
                        {angajat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('');
                    setFilterAngajat('');
                  }}
                >
                  Resetează
                </Button>
              </Grid>
            </Grid>
            {(user?.role === 'paroh' || user?.role === 'episcop' || user?.role === 'vicar') && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyMyPontaje}
                      onChange={(e) => setShowOnlyMyPontaje(e.target.checked)}
                    />
                  }
                  label="Arată doar pontajele mele"
                />
              </Box>
            )}
          </Paper>

          {/* Tabel pontaje */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Angajat</TableCell>
                  <TableCell>Funcție</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Ore lucrate</TableCell>
                  <TableCell>Ore suplimentare</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPontaje.map((pontaj) => (
                  <TableRow key={pontaj.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                          <PersonIcon />
                        </Avatar>
                        {pontaj.angajat}
                      </Box>
                    </TableCell>
                    <TableCell>{pontaj.functie}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {pontaj.data}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {pontaj.oraIntrare} - {pontaj.oraIesire}
                        <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                          ({pontaj.oreLucrate}h)
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {pontaj.oreSuplimentare > 0 ? (
                        <Chip
                          label={`+${pontaj.oreSuplimentare}h`}
                          color="warning"
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(pontaj.status)}
                        color={getStatusColor(pontaj.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewPontaj(pontaj)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {hasPermission('pontaje.write') && (
                        <IconButton color="secondary">
                          <EditIcon />
                        </IconButton>
                      )}
                      {hasPermission('pontaje.write') && pontaj.status === 'submitted' && (
                        <>
                          <IconButton
                            onClick={() => handleApprove(pontaj.id)}
                            color="success"
                          >
                            <ApproveIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleReject(pontaj.id)}
                            color="error"
                          >
                            <RejectIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab-uri pentru statusuri specifice */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Pontaje în așteptare de aprobare
          </Typography>
          <List>
            {pontaje.filter(p => p.status === 'submitted').map((pontaj) => (
              <React.Fragment key={pontaj.id}>
                <ListItem>
                  <ListItemIcon>
                    <TimeIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${pontaj.angajat} - ${pontaj.functie}`}
                    secondary={`${pontaj.data} • ${pontaj.oraIntrare} - ${pontaj.oraIesire} (${pontaj.oreLucrate}h)`}
                  />
                  <Box>
                    {pontaj.oreSuplimentare > 0 && (
                      <Chip
                        label={`+${pontaj.oreSuplimentare}h`}
                        color="warning"
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    )}
                    {hasPermission('pontaje.write') && (
                      <>
                        <IconButton
                          onClick={() => handleApprove(pontaj.id)}
                          color="success"
                          size="small"
                        >
                          <ApproveIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleReject(pontaj.id)}
                          color="error"
                          size="small"
                        >
                          <RejectIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Pontaje aprobate
          </Typography>
          <List>
            {pontaje.filter(p => p.status === 'approved').map((pontaj) => (
              <React.Fragment key={pontaj.id}>
                <ListItem>
                  <ListItemIcon>
                    <ApproveIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${pontaj.angajat} - ${pontaj.functie}`}
                    secondary={`${pontaj.data} • ${pontaj.oraIntrare} - ${pontaj.oraIesire} • Aprobat de: ${pontaj.aprobatDe}`}
                  />
                  <Box>
                    {pontaj.oreSuplimentare > 0 && (
                      <Chip
                        label={`+${pontaj.oreSuplimentare}h`}
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Pontaje respinse
          </Typography>
          <List>
            {pontaje.filter(p => p.status === 'rejected').map((pontaj) => (
              <React.Fragment key={pontaj.id}>
                <ListItem>
                  <ListItemIcon>
                    <RejectIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${pontaj.angajat} - ${pontaj.functie}`}
                    secondary={`${pontaj.data} • ${pontaj.oraIntrare} - ${pontaj.oraIesire} • ${pontaj.observatii}`}
                  />
                  <Box>
                    {pontaj.oreSuplimentare > 0 && (
                      <Chip
                        label={`+${pontaj.oreSuplimentare}h`}
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>
      </Paper>

      {/* Dialog pentru vizualizare pontaj */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii pontaj - {selectedPontaj?.angajat}
        </DialogTitle>
        <DialogContent>
          {selectedPontaj && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Angajat
                </Typography>
                <Typography variant="body1">
                  {selectedPontaj.angajat}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Funcție
                </Typography>
                <Typography variant="body1">
                  {selectedPontaj.functie}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data
                </Typography>
                <Typography variant="body1">
                  {selectedPontaj.data}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Program
                </Typography>
                <Typography variant="body1">
                  {selectedPontaj.oraIntrare} - {selectedPontaj.oraIesire}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Ore lucrate
                </Typography>
                <Typography variant="body1">
                  {selectedPontaj.oreLucrate} ore
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Ore suplimentare
                </Typography>
                <Typography variant="body1">
                  {selectedPontaj.oreSuplimentare} ore
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={getStatusLabel(selectedPontaj.status)}
                  color={getStatusColor(selectedPontaj.status) as any}
                />
              </Grid>
              {selectedPontaj.aprobatDe && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Aprobat de
                  </Typography>
                  <Typography variant="body1">
                    {selectedPontaj.aprobatDe}
                  </Typography>
                </Grid>
              )}
              {selectedPontaj.observatii && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedPontaj.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          {hasPermission('pontaje.write') && selectedPontaj?.status === 'submitted' && (
            <>
              <Button
                onClick={() => handleApprove(selectedPontaj!.id)}
                color="success"
                startIcon={<ApproveIcon />}
              >
                Aprobă
              </Button>
              <Button
                onClick={() => handleReject(selectedPontaj!.id)}
                color="error"
                startIcon={<RejectIcon />}
              >
                Respinge
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pontaje;
